/**
 * Notifications panel — bell trigger with dropdown anchored top-right
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, X, CreditCard, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

const PANEL_WIDTH = 360;
const PANEL_MAX_HEIGHT = 420;
const GAP = 8;

const NotificationsDropdown = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [panelStyle, setPanelStyle] = useState({});
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const updatePanelPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const top = rect.bottom + GAP;
    let left = rect.right - PANEL_WIDTH;
    left = Math.max(GAP, Math.min(left, window.innerWidth - PANEL_WIDTH - GAP));

    const maxHeight = Math.min(
      PANEL_MAX_HEIGHT,
      window.innerHeight - top - GAP
    );

    setPanelStyle({
      position: 'fixed',
      top,
      left,
      width: PANEL_WIDTH,
      maxHeight,
      zIndex: 9999,
    });
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!isOpen) return;

    updatePanelPosition();
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);

    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [isOpen, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API_URL}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API_URL}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNotifications();
      toast.success(t('markAllRead'));
    } catch {
      toast.error('Failed to mark all as read');
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'payment_success':
        return <Check className="w-4 h-4 text-emerald-500" />;
      case 'payment_failed':
        return <X className="w-4 h-4 text-red-500" />;
      case 'split_bill':
        return <CreditCard className="w-4 h-4 text-primary" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const panel = isOpen
    ? createPortal(
        <div
          ref={panelRef}
          style={panelStyle}
          className="bg-card border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150"
          role="dialog"
          aria-label={t('notifications')}
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0 bg-muted/30">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">{t('notifications')}</h3>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium bg-primary/15 text-primary rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                disabled={loading}
                className="text-xs text-primary hover:underline disabled:opacity-50"
              >
                {t('markAllRead')}
              </button>
            )}
          </div>

          <div className="overflow-y-auto flex-1 min-h-0">
            {notifications.length > 0 ? (
              notifications.slice(0, 15).map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b border-border/50 hover:bg-muted/50 cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => {
                    if (!notification.is_read) markAsRead(notification.id);
                    const alertType = notification.data?.alert_type;
                    const actionUrl = (alertType === 'scan_nudge' || alertType === 'low_balance')
                      ? notification.data?.url
                      : null;
                    if (actionUrl) {
                      setIsOpen(false);
                      navigate(actionUrl);
                      return;
                    }
                    if (
                      notification.type === 'payment_success' ||
                      notification.type === 'fulfillment_failed'
                    ) {
                      setIsOpen(false);
                      navigate('/history');
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-snug">
                        {notification.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground/80 mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <div className="w-2 h-2 bg-primary rounded-full shrink-0 mt-1.5" aria-hidden />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-12 px-4 text-center text-muted-foreground">
                <Bell className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm font-medium">{t('noNotifications')}</p>
                <p className="text-xs mt-1 opacity-70">You&apos;re all caught up</p>
              </div>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setIsOpen((open) => {
            const next = !open;
            if (next) requestAnimationFrame(updatePanelPosition);
            return next;
          });
        }}
        className={`relative p-2 rounded-lg transition-all ${
          isOpen ? 'bg-muted text-foreground' : 'hover:bg-muted text-muted-foreground hover:text-foreground'
        }`}
        data-testid="notifications-btn"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={t('notifications')}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-primary text-primary-foreground text-[10px] font-semibold rounded-full flex items-center justify-center ring-2 ring-background">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {panel}
    </>
  );
};

export default NotificationsDropdown;
